import { lazy, type ComponentType, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { Entrada, Protegido, VueltaDelAcceso } from "./components/Sesion";
import EngineDemo from "./pages/EngineDemo";
import Informe, { InformeCompartido } from "./pages/Informe";
import Resultados from "./pages/Resultados";
import Plan from "./pages/Plan";
import Wizard, { NuevaSesion } from "./pages/Wizard";

// El método se descarga solo al entrar en él: son sus pantallas enteras, y
// quien no las usa —hoy, todo el que no está en la lista de probadores— no
// tiene por qué pagarlas en cada visita.
const metodo = () => import("./components/Metodo");
const diferida = (nombre: string) =>
  lazy(async () => {
    const componente = ((await metodo()) as unknown as Record<string, ComponentType | undefined>)[nombre];
    if (!componente) throw new Error(`El método no exporta ${nombre}`);
    return { default: componente };
  });
const MarcoMetodo = diferida("MarcoMetodo");
const EntradaMetodo = diferida("EntradaMetodo");
const MethodAccessRoute = diferida("MethodAccessRoute");
const Bienvenida = diferida("Bienvenida");
const Proceso = diferida("Proceso");
const Mapa = diferida("Mapa");
const Mision = diferida("Mision");
const Destino = diferida("Destino");
const Perfil = diferida("Perfil");

export default function App() {
  return (
    <Routes>
      {/* La raiz lleva al acceso: quien teclea app.critero.es viene a entrar.
          Lo comercial vive en critero.es desde septiembre. */}
      <Route path="/" element={<Entrada />} />

      {/* ── Sin cuenta ───────────────────────────────────────────────────────
          El informe compartido abre por su enlace porque el token ES la
          autorización: quien lo recibe puede ser la pareja de alguien o el jefe
          con quien negocia, y no va a crearse una cuenta para leerlo.

          El test de nueve preguntas ya no está. El punto de entrada gratuito es
          el diagnóstico completo tras registrarse: se regala entero porque lo
          que se vende no es saber qué hacer, sino que te ayuden a hacerlo. */}
      <Route path="/demo" element={<EngineDemo />} />
      <Route path="/r/:token" element={<InformeCompartido />} />
      <Route path="/acceso/vuelta" element={<VueltaDelAcceso />} />

      {/* `/test` era el test de nueve preguntas. Quien lo tenga guardado, o
          llegue desde un enlace viejo, va a su sucesor en vez de encontrarse una
          pantalla en blanco: la SPA sirve el index para cualquier ruta, así que
          sin esto no hay ni 404, solo nada. */}
      <Route path="/test" element={<Navigate to="/cuestionario" replace />} />

      {/* ── Con cuenta ─────────────────────────────────────────────────────── */}
      <Route
        path="/resultados"
        element={
          <Protegido>
            <Resultados />
          </Protegido>
        }
      />
      <Route
        path="/cuestionario"
        element={
          <Protegido>
            <NuevaSesion />
          </Protegido>
        }
      />
      <Route
        path="/cuestionario/:sessionId/:step"
        element={
          <Protegido>
            <Wizard />
          </Protegido>
        }
      />
      <Route
        path="/plan/:sessionId"
        element={
          <Protegido>
            <Plan />
          </Protegido>
        }
      />
      <Route
        path="/informe/:sessionId"
        element={
          <Protegido>
            <Informe />
          </Protegido>
        }
      />

      {/* ── El método: Mapa, Misión, Destino y Perfil ──────────────────────────
          Sus pantallas, con nuestro login y su marco. Mientras se prueban solo
          las ve la lista de probadores; al resto, `MarcoMetodo` los devuelve a
          sus resultados. `/acceso` entrega el diagnóstico y abre el proceso; el
          resto de rutas son las suyas, con los mismos nombres, para no tocar
          sus enlaces internos. */}
      <Route
        element={
          <Protegido>
            <Suspense fallback={null}>
              <MarcoMetodo />
            </Suspense>
          </Protegido>
        }
      >
        <Route path="/acceso" element={<EntradaMetodo />} />
        <Route path="/perfil" element={<Perfil />} />
        <Route element={<MethodAccessRoute />}>
          <Route path="/bienvenida" element={<Bienvenida />} />
          <Route path="/proceso" element={<Proceso />} />
          <Route path="/mapa" element={<Mapa />} />
          <Route path="/mision/:missionPlanId" element={<Mision />} />
          <Route path="/destino/:missionPlanId" element={<Destino />} />
        </Route>
      </Route>

      {/* Cualquier otra cosa, a la entrada. Mismo motivo: aquí no hay servidor
          que devuelva un 404, solo el router. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
